import { Box } from 'grommet'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'

import useDeviceSize from 'hooks/useDevice'
import EmotionButton from 'components/emotion/EmotionButton'
import { BasicEmotion, BasicMood, Emotion } from 'db-tables'
import { INITIAL_DIAL_ANGLE, useProvider } from 'hooks/useMoodPlot'
import AdvancedLineButton from 'components/form/AdvancedLineButton'
import emotionWordsByAngle, { MAX_NUMBER_OF_WORDS } from 'util/emotionWordsByAngle'

import GridWrapper from './GridWrapper'
import { trackEvent } from 'utils/useGTM'

type Props = {
  angle: number | null
  emotions: Emotion[] | BasicEmotion[]
  maxNumberOfWords?: number
  selectedMoods: BasicMood[]
  onSelected: (emotion: Emotion | BasicEmotion, angle: number) => void
  disabled?: boolean
  showOthersWhenDisabled?: boolean
}
export function EmotionsList({
  angle,
  emotions,
  selectedMoods,
  disabled = false,
  showOthersWhenDisabled = true,
  onSelected,
}: Props) {
  const { t } = useTranslation()
  const { small } = useDeviceSize()
  const {
    state: { emotionWords },
  } = useProvider()
  const [emotionsLimit, setEmotionsLimit] = useState(MAX_NUMBER_OF_WORDS)
  const [lastAngle, setLastAngle] = useState(angle)

  const availableWords = useMemo(() => {
    if (!showOthersWhenDisabled && disabled) {
      return emotions.filter((emotion) =>
        selectedMoods.some((selected) => selected.emotion_id === emotion.id),
      )
    }
    return emotionWordsByAngle({
      angle: angle ?? INITIAL_DIAL_ANGLE,
      emotionWords: emotions,
      limit: emotionsLimit,
      selectedEmotionWords: emotionWords,
    })
  }, [
    showOthersWhenDisabled,
    disabled,
    angle,
    emotions,
    emotionsLimit,
    emotionWords,
    selectedMoods,
  ])

  useEffect(() => {
    if (angle !== lastAngle) {
      // Reset the limit and hide additional emotions when angle changes
      setEmotionsLimit(MAX_NUMBER_OF_WORDS)
    }
  }, [angle, lastAngle])

  const showAdvanced = useMemo(
    () => availableWords.length > emotionsLimit,
    [availableWords.length, emotionsLimit],
  )

  const updateDisplayedNumberOfEmotions = useCallback(() => {
    if (availableWords.length >= emotionsLimit) {
      setEmotionsLimit(emotionsLimit + MAX_NUMBER_OF_WORDS)
      setLastAngle(angle)
      trackEvent({ event: 'ShowMoreEmotions' })
    }
  }, [angle, availableWords.length, emotionsLimit])

  const isMoodSelected = useCallback(
    (emotionId: string) => selectedMoods.some((mood) => mood.emotion_id === emotionId),
    [selectedMoods],
  )

  if (angle === null) {
    return null
  }

  return (
    <>
      <GridWrapper desktopColumns={disabled && !showOthersWhenDisabled ? 1 : 2}>
        {availableWords.slice(0, emotionsLimit).map((emotion) => (
          <EmotionButton
            key={emotion.id}
            emotion={emotion}
            disabled={disabled}
            selected={isMoodSelected(emotion.id)}
            onClick={() => onSelected(emotion, angle)}
          />
        ))}
      </GridWrapper>
      {showAdvanced && (
        <Box margin={{ top: small ? 'large' : 'small' }}>
          <AdvancedLineButton
            isOpened={!showAdvanced}
            onToggle={updateDisplayedNumberOfEmotions}
            labelText={t('cta.show-more')}
          />
        </Box>
      )}
    </>
  )
}
